import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { PayService } from 'src/app/service/pay.service';
import { ApiResponse } from 'src/app/shared/api-response';

interface Pays {
  id?: number;
  nom: string;
  code: string;
  sigleMonnaie?: string;
}

interface MemberCreationPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  codePays: string;
  nidentification: string;
}

interface AdminMemberProvisioningData {
  userId: number;
  email: string;
  phone: string;
  primaryAccountId: number;
  primaryAccountNumber: number;
  memberId: string;
  externalAccountNumber: string;
}

type UploadType = 'profile' | 'recto' | 'verso';

type MemberForm = {
  nidentification: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  phone: FormControl<string>;
  codePays: FormControl<string>;
};

type MemberControlName = keyof MemberForm;

@Component({
  selector: 'app-ajouter-membre',
  templateUrl: './ajouter-membre.component.html',
  styleUrls: ['./ajouter-membre.component.css'],
  standalone: false
})
export class AjouterMembreComponent implements OnInit {
  signUp!: FormGroup<MemberForm>;

  emailexist = false;
  phonexist = false;
  loader = false;

  selectedProfileFile: File | null = null;
  selectedRectoFile: File | null = null;
  selectedVersoFile: File | null = null;

  selectedProfileName = '';
  selectedRectoName = '';
  selectedVersoName = '';

  payslist: Pays[] = [];

  currentStep = 1;
  readonly totalSteps = 4;

  successMessage = '';
  errorMessage = '';
  provisionedMember?: AdminMemberProvisioningData;

  private readonly stepOneControls: MemberControlName[] = [
    'nidentification'
  ];

  private readonly stepTwoControls: MemberControlName[] = [
    'firstName',
    'lastName'
  ];

  private readonly stepThreeControls: MemberControlName[] = [
    'email',
    'phone',
    'codePays'
  ];

  constructor(private userService: PayService) { }

  ngOnInit(): void {
    this.ngOnSignUp();
    this.getPays();
  }

  getPays(): void {
    this.userService.getlistPays().pipe(take(1)).subscribe((data: Pays[]) => {
      this.payslist = data ?? [];
    });
  }

  ngOnSignUp(): void {
    this.signUp = new FormGroup<MemberForm>({
      nidentification: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(6), Validators.maxLength(40)]
      }),
      firstName: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50)]
      }),
      lastName: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50)]
      }),
      email: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email]
      }),
      phone: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern('[1-9][0-9]*'),
          Validators.pattern('(^(65)[0-9]{1}[0-9]{6}|^(69|66|67|64)[0-9]{7})$')
        ]
      }),
      codePays: new FormControl<string>('237', {
        nonNullable: true,
        validators: [Validators.required]
      })
    });
  }

  onDocumentSelected(event: Event, type: UploadType): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;

    if (!file) {
      return;
    }

    this.errorMessage = '';

    if (type === 'profile') {
      this.selectedProfileFile = file;
      this.selectedProfileName = file.name;
      return;
    }

    if (type === 'recto') {
      this.selectedRectoFile = file;
      this.selectedRectoName = file.name;
      return;
    }

    this.selectedVersoFile = file;
    this.selectedVersoName = file.name;
  }

  nextStep(): void {
    if (!this.signUp) {
      return;
    }

    if (this.currentStep === 1) {
      this.markStepTouched(this.stepOneControls);
      if (!this.isStepOneValid()) {
        return;
      }
      this.currentStep = 2;
      return;
    }

    if (this.currentStep === 2) {
      this.markStepTouched(this.stepTwoControls);
      if (!this.isStepTwoValid()) {
        return;
      }
      this.currentStep = 3;
      return;
    }

    if (this.currentStep === 3) {
      this.markStepTouched(this.stepThreeControls);
      if (!this.isStepThreeValid()) {
        return;
      }
      this.currentStep = 4;
      return;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep -= 1;
    }
  }

  onSubmit(): void {
    this.clearMessages();

    if (this.currentStep < this.totalSteps) {
      this.nextStep();
      return;
    }

    this.signUp.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    if (this.signUp.invalid || this.emailexist || this.phonexist || !this.isStepFourValid()) {
      this.signUp.markAllAsTouched();
      if (!this.isStepFourValid()) {
        this.errorMessage = 'Les trois images sont obligatoires: CNI recto, CNI verso et photo de profil.';
      }
      return;
    }

    const value = this.signUp.getRawValue();
    const payload: MemberCreationPayload = {
      nidentification: value.nidentification.trim(),
      firstName: value.firstName.trim(),
      lastName: value.lastName.trim(),
      email: value.email.trim(),
      phone: value.phone.trim(),
      codePays: value.codePays
    };

    this.loader = true;
    this.userService.ajouterMembreAdmin(payload).pipe(take(1)).subscribe({
      next: (response: ApiResponse<AdminMemberProvisioningData>) => {
        if (!response?.success) {
          this.loader = false;
          this.errorMessage = response?.message || 'La creation du membre a echoue';
          return;
        }

        this.provisionedMember = response.data;
        const usernameToUpload = (response.data?.email || payload.email || '').trim();
        if (!usernameToUpload) {
          this.loader = false;
          this.successMessage = response.message || 'Membre cree avec succes';
          return;
        }

        this.uploadIdentityDocuments(usernameToUpload, response.message || 'Membre cree avec succes');
      },
      error: (error) => {
        this.loader = false;
        this.errorMessage = error?.error?.message || 'Erreur serveur pendant la creation du membre';
      }
    });
  }

  private uploadIdentityDocuments(username: string, baseMessage: string): void {
    const uploads = [
      { file: this.selectedRectoFile, action: 2, label: 'CNI recto' },
      { file: this.selectedVersoFile, action: 1, label: 'CNI verso' },
      { file: this.selectedProfileFile, action: 5, label: 'Photo profil' }
    ];

    const uploadRequests = uploads.map((item) => {
      const formData = new FormData();
      formData.append('file', item.file as File, (item.file as File).name);
      return this.userService.uploadFile(formData, item.action, username).pipe(
        take(1),
        map(() => ({ label: item.label, success: true })),
        catchError(() => of({ label: item.label, success: false }))
      );
    });

    forkJoin(uploadRequests).pipe(take(1)).subscribe((results) => {
      this.loader = false;
      this.currentStep = this.totalSteps;

      const failed = results.filter(result => !result.success).map(result => result.label);
      if (failed.length === 0) {
        this.successMessage = `${baseMessage}. Documents envoyes avec succes.`;
        return;
      }

      this.successMessage = `${baseMessage}.`;
      this.errorMessage = `Membre cree, mais echec upload: ${failed.join(', ')}.`;
    });
  }

  private clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

  private markStepTouched(controlNames: MemberControlName[]): void {
    controlNames.forEach(controlName => this.signUp.controls[controlName].markAsTouched());
  }

  private allControlsValid(controlNames: MemberControlName[]): boolean {
    return controlNames.every(controlName => this.signUp.controls[controlName].valid);
  }

  private hasAllRequiredDocuments(): boolean {
    return !!this.selectedProfileFile && !!this.selectedRectoFile && !!this.selectedVersoFile;
  }

  isStepOneValid(): boolean {
    return this.allControlsValid(this.stepOneControls);
  }

  isStepTwoValid(): boolean {
    return this.allControlsValid(this.stepTwoControls);
  }

  isStepThreeValid(): boolean {
    return this.allControlsValid(this.stepThreeControls) && !this.emailexist && !this.phonexist;
  }

  isStepFourValid(): boolean {
    return this.hasAllRequiredDocuments();
  }

  stepProgress(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  get nidentification() { return this.signUp.controls.nidentification; }
  get firstName() { return this.signUp.controls.firstName; }
  get lastName() { return this.signUp.controls.lastName; }
  get email() { return this.signUp.controls.email; }
  get phone() { return this.signUp.controls.phone; }
  get codePays() { return this.signUp.controls.codePays; }

  isEmail(email: string | null): void {
    if (!email || this.email.invalid) {
      this.emailexist = false;
      return;
    }
    this.userService.findEmail(email).pipe(take(1)).subscribe(res => {
      this.emailexist = !res?.success;
    }, _ => this.emailexist = false);
  }

  isPhone(phone: string | null): void {
    if (!phone || this.phone.invalid) {
      this.phonexist = false;
      return;
    }
    this.userService.findPhone(phone).pipe(take(1)).subscribe(res => {
      this.phonexist = !res?.success;
    }, _ => this.phonexist = false);
  }
}
